import { useReadContract } from 'wagmi'
import { operatorRolesAbi } from '@/abi/operatorRolesAbi'
import { getContractAddress } from '@/config/contracts'
import { DEFAULT_ADMIN_ROLE, MLRO_ROLE, READ_ONLY_ROLE } from '@/config/onchain-roles'
import { matchesEvent } from '@/lib/eventStream'
import { useStreamInvalidation } from './_streamInvalidation'

type Holders = readonly `0x${string}`[]

// Enumerates the holders of each console role on `chainId` via the enumerable
// `getRoleMembers(role)` view, and invalidates the cached reads whenever the
// service event stream relays `RoleGranted` / `RoleRevoked`, so the lists stay
// live as the operator grants/revokes. Modeled on unipot's `useAdmins` / `useOperators`.
export function useConsoleRoleMembers(chainId: number) {
    const registry = getContractAddress(chainId, 'operatorRoles')
    const enabled = Boolean(registry)

    const admins = useReadContract({
        address: registry,
        abi: operatorRolesAbi,
        functionName: 'getRoleMembers',
        args: [DEFAULT_ADMIN_ROLE],
        chainId,
        query: { enabled },
    })
    const mlros = useReadContract({
        address: registry,
        abi: operatorRolesAbi,
        functionName: 'getRoleMembers',
        args: [MLRO_ROLE],
        chainId,
        query: { enabled },
    })
    const readOnly = useReadContract({
        address: registry,
        abi: operatorRolesAbi,
        functionName: 'getRoleMembers',
        args: [READ_ONLY_ROLE],
        chainId,
        query: { enabled },
    })

    useStreamInvalidation(
        chainId,
        enabled,
        (m) =>
            matchesEvent(m, 'operatorRoles', 'RoleGranted') ||
            matchesEvent(m, 'operatorRoles', 'RoleRevoked'),
        [admins.queryKey, mlros.queryKey, readOnly.queryKey],
    )

    return {
        admins: (admins.data ?? []) as Holders,
        mlros: (mlros.data ?? []) as Holders,
        readOnly: (readOnly.data ?? []) as Holders,
        isLoading: admins.isLoading || mlros.isLoading || readOnly.isLoading,
        isError: admins.isError || mlros.isError || readOnly.isError,
    }
}
